<template>
    <div class="settings">
        <section class="settings-items">
            <template v-for="view in settingViews">
                <component
                    :is="views[view.id]"
                    :key="$route.params.portfolioNumber + view.id"
                    :mwcId="getMwcIdForChild(`hypo-settings-${view.id}`)"
                    :text="view.text"
                    v-bind="componentProps(view.id)"
                    @on-error="handleError"
                    @global-time-period-changed="handleGlobalTimePeriodChanged"
                    @security-time-period-changed="handleSecurityTimePeriodChanged"
                >
                </component>
            </template>
        </section>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import Holdings from "./Holdings/Index.vue";
import Rebalancing from "./Rebalancing/Index.vue";
import Transactions from "./Transactions/Index.vue";
import Fees from "./Fees/Index.vue";
import Taxes from "./Taxes/Index.vue";
import Benchmark from "./Benchmark/Index.vue";
import Reinvestment from "./Reinvestment/Index.vue";
import storeParams from "./store/index.js";
import Vuex, { mapGetters } from "vuex";
import * as types from "./store/mutation-types.js";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import cloneDeep from "lodash.clonedeep";

export default {
    name: "hypo-settings",
    props: {
        labels: {
            type: Object,
            default: () => {}
        },
        portfolioData: {
            type: Object,
            default: () => {}
        },
        preferences: {
            type: Object,
            default: () => {}
        },
        currencyList: {
            type: Array,
            default: () => []
        },
        settingItems: {
            type: Array,
            default: () => []
        },
        activeViews: {
            type: Array,
            default: () => []
        },
        assetAllocation: {
            type: Array,
            default: () => []
        },
        isApplyEarliestAvailable: Boolean,
        hypoType: {
            type: Number,
            default: 0
        },
        portfolioList: Array,
        globalAvailableDates: Object,
        globalTimePeriod: [Number, String],
        customTimePeriodDates: [Object]
    },
    store() {
        const modules = {};
        Object.keys(storeParams.modules).forEach(key => {
            const module = storeParams.modules[key];
            modules[key] = {
                ...module,
                state: cloneDeep(module.state)
            };
        });
        return new Vuex.Store({
            ...storeParams,
            modules: modules
        });
    },
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        [Holdings.name]: Holdings,
        [Rebalancing.name]: Rebalancing,
        [Transactions.name]: Transactions,
        [Fees.name]: Fees,
        [Taxes.name]: Taxes,
        [Benchmark.name]: Benchmark,
        [Reinvestment.name]: Reinvestment
    },
    data() {
        return {
            defaultConfig: {
                labels: this.labels
            },
            views: {
                HOLDINGS: Holdings.name,
                REBALANCING: Rebalancing.name,
                CONTRIWITHDRAW: Transactions.name,
                FEES: Fees.name,
                TAXES: Taxes.name,
                BENCHMARK: Benchmark.name,
                REINVESTMENTS: Reinvestment.name
            }
        };
    },
    created() {
        this.$store.commit(`settings/${types.SET_PREFERENCES}`, {
            preferences: cloneDeep(this.preferences),
            type: this.hypoType
        });
        this.$store.commit(`settings/${types.SET_IS_APPLY_EARLIEST_AVAILABLE}`, this.isApplyEarliestAvailable);
        this.$store.commit(`settings/${types.SET_CURRENCY_LIST}`, this.currencyList);
        this.$store.commit(`settings/${types.SET_ASSET_ALLOCATION}`, this.assetAllocation);
        const portfolio = this.portfolioData;
        this.$store.dispatch("settings/setHypoType", this.hypoType);
        this.$store.dispatch("settings/initSettings", portfolio);
        this.$store.commit("labels/setLabels", this.labels);
    },
    computed: {
        ...mapGetters({
            newPortfolioData: "settings/portfolioData"
        }),
        settingViews() {
            return this.settingItems.map(item => {
                return {
                    id: item.value,
                    text: item.labelKey
                };
            });
        },
        hasDataChange() {
            return this.$store.getters["settings/hasDataChange"];
        },
        languageId() {
            return this.$store.state.settings.preferences.languageId;
        },
        componentProps() {
            return id => {
                if (id === "HOLDINGS") {
                    return {
                        isPortfolioComparison: this.hypoType === 1,
                        portfolioList: this.portfolioList,
                        globalAvailableDates: this.globalAvailableDates,
                        globalTimePeriod: this.globalTimePeriod,
                        customTimePeriodDates: this.customTimePeriodDates
                    };
                }
                return null;
            };
        }
    },
    watch: {
        newPortfolioData: {
            handler: function() {
                if (!this.hasDataChange) {
                    return;
                }
                this.$emit("change", this.newPortfolioData, this.$store.state.settings.rawPortfolio);
            }
        },
        preferences(newValue, oldValue) {
            this.$store.commit(`settings/${types.SET_PREFERENCES}`, this.preferences);
            if (newValue.timePeriod !== oldValue.timePeriod) {
                this.$store.dispatch("holdings/changeTimePeriod", newValue.timePeriod);
            }
        },
        currencyList() {
            this.$store.commit(`settings/${types.SET_CURRENCY_LIST}`, this.currencyList);
        }
    },
    methods: {
        handleError(error) {
            this.$emit("component-error", error);
        },
        handleGlobalTimePeriodChanged(payload) {
            this.$emit("global-time-period-changed", payload);
        },
        handleSecurityTimePeriodChanged(payload) {
            this.$emit("security-time-period-changed", payload);
        }
    },
    destroyed() {
        this.$store.dispatch("settings/clearSettings");
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/typography";
.settings {
    &-items {
        ::v-deep .settings-panel {
            display: flex; //Block Format Context
            flex-direction: column;
            .settings-panel__inner {
                padding-bottom: $mds-space-3-x;
            }

            .mds-accordion__trigger:disabled + .mds-accordion__label {
                cursor: default;
            }

            .mds-accordion .mds-header.mds-header--secondary {
                margin-bottom: 10px;
            }
        }
    }

    ::v-deep .summary-label {
        @include mds-body-text-s($bold: true);
        color: $mds-text-color-primary;
    }

    ::v-deep .summary-text {
        @include mds-body-text-s($bold: false);
        color: $mds-text-color-primary;
    }

    ::v-deep .summary-label--none {
        @include mds-body-text-s($bold: false);
        color: $mds-text-color-secondary;
        margin-bottom: $mds-space-1-x;
    }
}
</style>
